/*
 * Trekano — continuous scroll + scroll-position restore for collection pages.
 * Layers on top of Focal's <product-facet> AJAX paging without touching theme.js.
 *
 * Behaviour:
 *  - Hides Focal's numbered pagination (kept in DOM as a no-JS fallback) and
 *    auto-appends the next page as you scroll near the bottom.
 *  - Re-initialises after a filter/sort change (Focal replaces #facet-main).
 *  - Saves scroll position + how many pages were loaded; when you return to the
 *    collection via the back button it re-expands and scrolls you back.
 */
(function () {
  'use strict';
  if (window.__trkInfiniteCollection) return;
  window.__trkInfiniteCollection = true;

  var SUPPORTS_IO = 'IntersectionObserver' in window;
  var MAX_RESTORE_PAGES = 40;
  var STALE_MS = 30 * 60 * 1000;

  if ('scrollRestoration' in history) {
    // We manage scroll restoration for collection back-navigation ourselves.
    history.scrollRestoration = 'manual';
  }

  function stateKey() {
    return 'trk_inf:' + location.pathname + location.search;
  }

  function navType() {
    try {
      var nav = performance.getEntriesByType('navigation')[0];
      return nav ? nav.type : '';
    } catch (e) {
      return '';
    }
  }

  function Controller(facet) {
    this.facet = facet;
    this.sectionId = facet.getAttribute('section-id');
    this.facetMain = facet.querySelector('#facet-main');
    this.loadedPages = 1;
    this.loading = false;
    this.nextUrl = null;
    this.io = null;

    var self = this;

    this.setup();

    // Focal replaces #facet-main's children on filter/sort change. Detect a
    // genuine product-list swap (not our own sentinel/footer insertions).
    if (this.facetMain && 'MutationObserver' in window) {
      this.domObserver = new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          var added = records[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (n.nodeType === 1 &&
              ((n.matches && n.matches('product-list, .product-facet__product-list')) ||
                (n.querySelector && n.querySelector('.product-list__inner')))) {
              self.loadedPages = 1;
              self.setup();
              return;
            }
          }
        }
      });
      this.domObserver.observe(this.facetMain, { childList: true, subtree: true });
    }

    // Persist position when leaving for a product (and on any navigation away).
    facet.addEventListener('click', function (e) {
      var a = e.target.closest ? e.target.closest('a[href*="/products/"]') : null;
      if (a) self.saveState();
    });
    window.addEventListener('pagehide', function () { self.saveState(); });

    this.maybeRestore();
  }

  Controller.prototype.grid = function () {
    return this.facet.querySelector('.product-list__inner');
  };

  Controller.prototype._nextFrom = function (pagination) {
    if (!pagination) return null;
    var a = pagination.querySelector('a[rel="next"]');
    return a ? a.href : null;
  };

  Controller.prototype.setup = function () {
    var grid = this.grid();
    if (!grid) return;

    this.pagination = this.facet.querySelector('.pagination');
    this.nextUrl = this._nextFrom(this.pagination);
    if (this.pagination) this.pagination.style.display = 'none';

    var list = this.facet.querySelector('.product-facet__product-list');
    var anchor = list || grid.parentNode;

    if (!this.footer || !this.facet.contains(this.footer)) {
      this.footer = document.createElement('div');
      this.footer.className = 'trk-inf-footer';
      this.footer.innerHTML =
        '<div class="trk-inf-status" role="status" aria-live="polite"></div>' +
        '<button type="button" class="button button--outline trk-inf-more" hidden>Load more products</button>';
      if (anchor && anchor.parentNode) {
        anchor.parentNode.insertBefore(this.footer, anchor.nextSibling);
      } else {
        grid.parentNode.appendChild(this.footer);
      }
      var self = this;
      this.footer.querySelector('.trk-inf-more').addEventListener('click', function () { self.loadNext(); });
    }
    this.moreBtn = this.footer.querySelector('.trk-inf-more');
    this.statusEl = this.footer.querySelector('.trk-inf-status');

    if (!this.sentinel || !this.facet.contains(this.sentinel)) {
      this.sentinel = document.createElement('div');
      this.sentinel.className = 'trk-inf-sentinel';
      this.sentinel.setAttribute('aria-hidden', 'true');
      this.footer.parentNode.insertBefore(this.sentinel, this.footer);
    }

    this._reflectButton();
    this._observe();
  };

  Controller.prototype._reflectButton = function () {
    if (!this.moreBtn) return;
    // With IntersectionObserver we auto-load; the button is only a fallback.
    // NOTE: theme .button CSS (display) beats the UA [hidden] rule, so hide inline too.
    var show = !!(this.nextUrl && !SUPPORTS_IO);
    this.moreBtn.hidden = !show;
    this.moreBtn.style.display = show ? '' : 'none';
  };

  Controller.prototype._observe = function () {
    if (!SUPPORTS_IO || !this.nextUrl || !this.sentinel) return;
    if (this.io) this.io.disconnect();
    var self = this;
    this.io = new IntersectionObserver(function (entries) {
      if (entries[0] && entries[0].isIntersecting) self.loadNext();
    }, { rootMargin: '900px 0px' });
    this.io.observe(this.sentinel);
  };

  Controller.prototype.loadNext = function () {
    var self = this;
    if (this.loading || !this.nextUrl) return Promise.resolve();
    this.loading = true;
    this.facet.classList.add('trk-inf-loading');
    if (this.moreBtn) this.moreBtn.setAttribute('aria-busy', 'true');

    var url;
    try {
      url = new URL(this.nextUrl, location.origin);
    } catch (e) {
      this.loading = false;
      return Promise.resolve();
    }
    url.searchParams.set('section_id', this.sectionId);

    return fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newInner = doc.querySelector('.product-list__inner');
        var grid = self.grid();
        if (newInner && grid) {
          var nodes = Array.prototype.slice.call(newInner.children);
          for (var i = 0; i < nodes.length; i++) {
            var node = nodes[i];
            if (node.removeAttribute) {
              node.removeAttribute('reveal');
              node.removeAttribute('reveal-visibility');
            }
            grid.appendChild(document.importNode(node, true));
          }
        }
        self.nextUrl = self._nextFrom(doc.querySelector('.pagination'));
        self.loadedPages += 1;
        self._reflectButton();
        if (!self.nextUrl && self.io) self.io.disconnect();
      })
      .catch(function () {
        // On failure, restore the native numbered pagination as a fallback.
        if (self.pagination) self.pagination.style.display = '';
      })
      .then(function () {
        self.loading = false;
        self.facet.classList.remove('trk-inf-loading');
        if (self.moreBtn) self.moreBtn.removeAttribute('aria-busy');
      });
  };

  Controller.prototype.saveState = function () {
    try {
      sessionStorage.setItem(stateKey(), JSON.stringify({
        y: window.scrollY || window.pageYOffset || 0,
        pages: this.loadedPages,
        t: Date.now()
      }));
    } catch (e) {}
  };

  Controller.prototype.maybeRestore = function () {
    var raw;
    try { raw = sessionStorage.getItem(stateKey()); } catch (e) { return; }
    if (!raw) return;
    try { sessionStorage.removeItem(stateKey()); } catch (e) {}

    if (navType() !== 'back_forward') return;

    var state;
    try { state = JSON.parse(raw); } catch (e) { return; }
    if (!state || (Date.now() - (state.t || 0)) > STALE_MS) return;

    var self = this;
    var target = Math.min(state.pages || 1, MAX_RESTORE_PAGES);

    function settle() {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { window.scrollTo(0, state.y || 0); });
      });
    }

    function step() {
      if (self.loadedPages >= target || !self.nextUrl) { settle(); return; }
      self.loadNext().then(step);
    }

    step();
  };

  function init() {
    var facet = document.querySelector('product-facet');
    if (facet) new Controller(facet);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
